export interface WorkflowTaskOrderValue {
  readonly externalItemId: string;
  readonly createdAt: string;
  readonly workflowState: string;
  readonly stagePosition?: { readonly phase: string } | null;
}

export interface WorkflowTaskProjection<T> {
  readonly visible: readonly T[];
  readonly expectedVisible: number;
  readonly hidden: number;
}

export interface WorkflowTaskPageLoadState {
  readonly shouldLoad: boolean;
  /** Changes whenever another API page contributes cards, even if none match the active stage filter. */
  readonly pageProgress: number;
}

export function workflowTaskPageLoadState(input: {
  readonly loadedCardCount: number;
  readonly visibleCardCount: number;
  readonly expectedVisibleCardCount: number;
  readonly hasMore: boolean;
  readonly showCompleted: boolean;
  readonly filteredTotalIsUnknown: boolean;
}): WorkflowTaskPageLoadState {
  return {
    shouldLoad: !input.showCompleted && input.hasMore && (
      input.filteredTotalIsUnknown || input.visibleCardCount < input.expectedVisibleCardCount
    ),
    pageProgress: input.loadedCardCount,
  };
}

/** One source of truth for the task rail in both graph and detail modes. */
export function projectWorkflowTasks<T extends WorkflowTaskOrderValue>(
  cards: readonly T[],
  terminalStageIds: ReadonlySet<string>,
  stageFilter: string | null,
  showCompleted: boolean,
  selectedCardId: string | null,
  totalCards: number,
  stageTotals?: Readonly<Record<string, number>>,
): WorkflowTaskProjection<T> {
  const scoped = stageFilter === null ? cards : cards.filter((card) => card.workflowState === stageFilter);
  const scopedTotal = stageFilter === null ? totalCards : (stageTotals?.[stageFilter] ?? scoped.length);
  const loadedDoneOutsideTerminal = scoped.filter((card) =>
    !terminalStageIds.has(card.workflowState) && card.stagePosition?.phase === 'done'
  ).length;
  const hidden = stageFilter === null
    ? (stageTotals === undefined
        ? cards.filter((card) => terminalStageIds.has(card.workflowState) || card.stagePosition?.phase === 'done').length
        : [...terminalStageIds].reduce((sum, stageId) => sum + (stageTotals[stageId] ?? 0), 0) + loadedDoneOutsideTerminal)
    : (terminalStageIds.has(stageFilter)
        ? scopedTotal
        : scoped.filter((card) => card.stagePosition?.phase === 'done').length);
  const selected = scoped.find((card) => card.externalItemId === selectedCardId);
  const selectedWouldBeHidden = selected !== undefined &&
    (terminalStageIds.has(selected.workflowState) || selected.stagePosition?.phase === 'done');
  return {
    visible: visibleWorkflowTasks(scoped, terminalStageIds, showCompleted, selectedCardId),
    expectedVisible: showCompleted ? scopedTotal : Math.max(0, scopedTotal - hidden) + (selectedWouldBeHidden ? 1 : 0),
    hidden: showCompleted ? 0 : hidden,
  };
}

export function visibleWorkflowTasks<T extends WorkflowTaskOrderValue>(
  cards: readonly T[],
  terminalStageIds: ReadonlySet<string>,
  showCompleted: boolean,
  selectedCardId: string | null,
): readonly T[] {
  return cards
    .filter((card) =>
      showCompleted ||
      card.externalItemId === selectedCardId ||
      (!terminalStageIds.has(card.workflowState) && card.stagePosition?.phase !== 'done'),
    )
    .sort(taskBrowserOrder);
}

export function stageTaskOrder(left: WorkflowTaskOrderValue, right: WorkflowTaskOrderValue): number {
  const phase = (card: WorkflowTaskOrderValue): number => card.stagePosition?.phase === 'in_progress' ? 0 : 1;
  return phase(left) - phase(right) || newestFirst(left, right);
}

export function taskBrowserOrder(left: WorkflowTaskOrderValue, right: WorkflowTaskOrderValue): number {
  return newestFirst(left, right);
}

function newestFirst(left: WorkflowTaskOrderValue, right: WorkflowTaskOrderValue): number {
  return right.createdAt.localeCompare(left.createdAt) || right.externalItemId.localeCompare(left.externalItemId);
}
